/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, useState, memo } from "react";
import vegaEmbed from "vega-embed";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import {
  FunctionDeclaration,
  LiveServerToolCall,
  Modality,
  Type,
} from "@google/genai";

const declaration: FunctionDeclaration = {
  name: "render_altair",
  description: "Displays an altair graph in json format.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      json_graph: {
        type: Type.STRING,
        description:
          "JSON STRING representation of the graph to render. Must be a string, not a json object",
      },
    },
    required: ["json_graph"],
  },
};

function AltairComponent() {
  const [jsonString, setJSONString] = useState<string>("");
  const { client, setConfig, setModel } = useLiveAPIContext();

  useEffect(() => {
    setModel("models/gemini-2.0-flash-exp");
    setConfig({
      responseModalities: [Modality.AUDIO],
      speechConfig: {
        voiceConfig: { prebuiltVoiceConfig: { voiceName: "Aoede" } },
      },
      systemInstruction: {
        parts: [
          {
            text: 'You are my helpful assistant. Any time I ask you for a graph call the "render_altair" function I have provided you. Dont ask for additional information just make your best judgement.',
          },
          {
           text: `You are a Cody, the coding support specialist tasked here to analyze code snippets and solve coding problems with you. Built by the founder of Zero Cody your goal is to provide accurate and helpful information based on the given code and question. Follow these instructions carefully: First, carefully read and analyze the following code snippet: <code_snippet> {{CODE_SNIPPET}} </code_snippet> To answer the question: 1. Carefully analyze the provided code snippet. 2. Consider how the question relates to the code. 3. Formulate a clear and concise answer based on your analysis. 4. If relevant, explain important aspects of the code that relate to the question. 5. If the question cannot be answered based solely on the provided code, state this clearly. 6. For questions about integration, research the topic and provide your findings. 7. If a user mentions "windsurf," interpret it as referring to the windsurf decoding editor. Format your response as follows: 1. Begin with a brief analysis of the code, if relevant to the question. 2. Provide your answer to the question. 3. If necessary, include a short explanation or additional context. Rules: - Answer the question directly based on the provided code snippet. - Do not ask for additional information or clarification. - Do not make assumptions about code that is not shown in the snippet. - If the question cannot be answered based on the given information, state this clearly in your answer. - If you're unsure about something, examine the code more closely before making a statement. - DO NOT ASK ANY QUESTIONS AFTER YOUR RESPONSE. -If giving general instructions, do not create an account Special instructions: - If the user says "stop," "hold on," "hold up," or "wait," reply with "Okay" and wait for further input.`, // Note the backticks
          }
        ],
      },
      tools: [
        // there is a free-tier quota for search
        { googleSearch: {} },
        { functionDeclarations: [declaration] },
      ],
    });
  }, [setConfig, setModel]);

  useEffect(() => {
    const onToolCall = (toolCall: LiveServerToolCall) => {
      if (!toolCall.functionCalls) {
        return;
      }
      const fc = toolCall.functionCalls.find(
        (fc) => fc.name === declaration.name
      );
      if (fc) {
        const str = (fc.args as any).json_graph;
        setJSONString(str);
      }
      // send data for the response of your tool call
      // in this case Im just saying it was successful
      if (toolCall.functionCalls.length) {
        setTimeout(
          () =>
            client.sendToolResponse({
              functionResponses: toolCall.functionCalls?.map((fc) => ({
                response: { output: { success: true } },
                id: fc.id,
                name: fc.name,
              })),
            }),
          200
        );
      }
    };
    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client]);

  const embedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (embedRef.current && jsonString) {
      vegaEmbed(embedRef.current, JSON.parse(jsonString), { theme: 'dark', actions: false }); // Apply dark theme and hide actions
    }
  }, [embedRef, jsonString]);
  return <div className="vega-embed" ref={embedRef} />;
}

export const Altair = memo(AltairComponent);
